import React, { useState, useMemo, useCallback } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar, Variable } from "lucide-react";
import { useSilaeVariables } from "@/hooks/useSilaeVariables";
import { VariableSelector } from "./VariableSelector";
import moment from "moment";
import { cn } from "@/lib/utils";

interface SilaeDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  allowSilaeDateVariables?: boolean;
  className?: string;
}

interface DateComponent {
  type: "fixed" | "variable";
  value: string;
}

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const MONTHS = [
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

const dateVariables = {
  day: [
    {
      code: moment().format("DD"),
      label: "aujourd'hui",
    },
    {
      code: "1",
      label: "1er du mois",
    },
    {
      code: "30",
      label: "Fin du mois",
    },
  ],
  month: [
    {
      code: "bul.mois",
      label: "mois du bulletin",
    },
    {
      code: "bul.mois-1",
      label: "mois précédent",
    },
    {
      code: "bul.mois+1",
      label: "mois suivant",
    },
    ...MONTHS.slice(1).map((month) => ({
      code: `bul.mois-${month.value}`,
      label: `mois N-${month.value}`,
    })),
    ...MONTHS.slice(1).map((month) => ({
      code: `bul.mois+${month.value}`,
      label: `mois N+${month.value}`,
    })),
  ],
  year: [
    {
      code: "bul.annee",
      label: "année du bulletin",
    },
    {
      code: `bul.annee-1`,
      label: `Année précédente`,
    },
    {
      code: `bul.annee+1`,
      label: `Année suivante`,
    },
    ...Array.from({ length: 25 }, (_, i) => ({
      code: `bul.annee-${i + 2}`,
      label: `année N-${i + 2}`,
    })),
    ...Array.from({ length: 25 }, (_, i) => ({
      code: `bul.annee+${i + 2}`,
      label: `année N+${i + 2}`,
    })),
  ],
};

export const SilaeDatePicker: React.FC<SilaeDatePickerProps> = ({
  value,
  onChange,
  label = "date",
  allowSilaeDateVariables = true,
  className,
}) => {
  const [mode, setMode] = useState<"date_builder" | "silae_variable">(
    "date_builder",
  );
  const [selectedSilaeVariable, setSelectedSilaeVariable] =
    useState<string>("");
  const [day, setDay] = useState<DateComponent>({ type: "fixed", value: "1" });
  const [month, setMonth] = useState<DateComponent>({
    type: "fixed",
    value: "1",
  });
  const [year, setYear] = useState<DateComponent>({
    type: "variable",
    value: "bul.annee",
  });

  // États pour le VariableSelector
  const [searchTerm, setSearchTerm] = useState("");
  const [selectorOpen, setSelectorOpen] = useState(false);

  // Hook pour les variables Silae depuis Redux
  const { variables: silaeVariables, findVariable } = useSilaeVariables();

  // Obtenir les variables SILAE de type Date
  const silaeeDateVariables = useMemo(() => {
    return silaeVariables
      .filter((variable) => variable.type === "date")
      .sort((a, b) => {
        // Trier par fréquence d'utilisation puis par nom
        if (a.isFrequentlyUsed && !b.isFrequentlyUsed) return -1;
        if (!a.isFrequentlyUsed && b.isFrequentlyUsed) return 1;
        return (a.label || a.code).localeCompare(b.label || b.code);
      });
  }, [silaeVariables]);

  // Variables fréquemment utilisées de type Date
  const frequentlyUsedDateVariables = useMemo(() => {
    return silaeeDateVariables.filter((variable) => variable.isFrequentlyUsed);
  }, [silaeeDateVariables]);

  // Variables filtrées selon le terme de recherche
  const filteredDateVariables = useMemo(() => {
    if (!searchTerm) return silaeeDateVariables;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return silaeeDateVariables.filter(
      (variable) =>
        (variable.label || variable.code)
          .toLowerCase()
          .includes(lowerSearchTerm) ||
        variable.code.toLowerCase().includes(lowerSearchTerm) ||
        variable.definition.toLowerCase().includes(lowerSearchTerm),
    );
  }, [silaeeDateVariables, searchTerm]);

  // Variable sélectionnée pour le VariableSelector
  const selectedVariable = useMemo(() => {
    return selectedSilaeVariable
      ? findVariable(selectedSilaeVariable) || null
      : null;
  }, [selectedSilaeVariable]);

  // Parser la valeur initiale si elle existe
  useMemo(() => {
    if (value) {
      // Vérifier si c'est une variable SILAE de type Date
      const isDateVariable = silaeeDateVariables.some(
        (variable) => variable.code === value,
      );

      if (isDateVariable) {
        setMode("silae_variable");
        setSelectedSilaeVariable(value);
      } else if (value.startsWith("Date(") && value.endsWith(")")) {
        setMode("date_builder");
        const content = value.slice(5, -1); // Enlever Date( et )
        const parts = content.split(",").map((p) => p.trim());

        if (parts.length === 3) {
          // Jour — toujours fixe, on force un chiffre valide (≥ 1)
          const dayNum = parseInt(parts[0], 10);
          setDay({ type: "fixed", value: dayNum >= 1 ? parts[0] : "1" });

          // Mois — si 0 ou non configuré, on repasse en variable SILAE par défaut
          if (parts[1].match(/^\d+$/) && parts[1] !== "0") {
            setMonth({ type: "fixed", value: parts[1] });
          } else if (parts[1] === "0" || parts[1] === "") {
            setMonth({ type: "variable", value: dateVariables.month[0].code });
          } else {
            setMonth({ type: "variable", value: parts[1] });
          }

          // Année — si 0 ou non configuré, on repasse en variable SILAE par défaut
          if (parts[2].match(/^\d+$/) && parts[2] !== "0") {
            setYear({ type: "fixed", value: parts[2] });
          } else if (parts[2] === "0" || parts[2] === "") {
            setYear({ type: "variable", value: dateVariables.year[0].code });
          } else {
            setYear({ type: "variable", value: parts[2] });
          }
        }
      }
    }
  }, [value, silaeeDateVariables]);

  // Générer le code SILAE réel (avec les codes)
  const generateSilaeCode = useCallback(() => {
    const dayValue = day.type === "fixed" ? day.value : day.value;
    const monthValue = month.type === "fixed" ? month.value : month.value;
    const yearValue = year.type === "fixed" ? year.value : year.value;

    return `Date(${dayValue}, ${monthValue}, ${yearValue})`;
  }, [day, month, year]);

  // Générer la valeur Date() à partir des composants (pour l'affichage)
  const generateDateValue = useCallback(() => {
    // Fonction pour obtenir le label d'un composant
    const getComponentLabel = (
      component: DateComponent,
      type: "day" | "month" | "year",
    ) => {
      if (component.type === "fixed") {
        if (type === "day") {
          return component.value;
        } else if (type === "month") {
          const monthObj = MONTHS.find(
            (m) => m.value.toString() === component.value,
          );
          return monthObj ? monthObj.label : component.value;
        } else if (type === "year") {
          return component.value;
        }
      } else {
        // Type variable - chercher dans dateVariables
        const variable = dateVariables[type].find(
          (item) => item.code === component.value,
        );
        return variable ? variable.label : component.value;
      }
      return component.value;
    };

    const dayLabel = getComponentLabel(day, "day");
    const monthLabel = getComponentLabel(month, "month");
    const yearLabel = getComponentLabel(year, "year");

    return `Date(${dayLabel}, ${monthLabel}, ${yearLabel})`;
  }, [day, month, year]);

  // Mettre à jour la valeur quand les composants changent ou le mode change
  React.useEffect(() => {
    let newValue = "";

    if (mode === "silae_variable") {
      newValue = selectedSilaeVariable;
    } else {
      newValue = generateSilaeCode();
    }

    if (newValue !== value && newValue !== "") {
      onChange(newValue);
    }
  }, [mode, selectedSilaeVariable, generateSilaeCode, onChange, value]);

  const renderComponentSelector = (
    component: DateComponent,
    setComponent: (component: DateComponent) => void,
    type: "day" | "month" | "year",
    label: string,
  ) => {
    const isVariable = component.type === "variable";
    // Le jour est toujours fixe
    const isDayType = type === "day";

    const toggleMode = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isVariable) {
        const defaultFixed =
          type === "month" ? "1" : new Date().getFullYear().toString();
        setComponent({ type: "fixed", value: defaultFixed });
      } else {
        const firstVar = dateVariables[type]?.[0]?.code ?? "";
        setComponent({ type: "variable", value: firstVar });
      }
    };

    return (
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </Label>

        <div className="flex items-stretch">
          <Select
            value={component.value}
            onValueChange={(val) =>
              setComponent({
                type: isVariable ? "variable" : "fixed",
                value: val,
              })
            }
          >
            <SelectTrigger
              className={cn(
                "h-9 flex-1 bg-background focus:ring-0 focus:ring-offset-0",
                isDayType ? "rounded-md" : "rounded-r-none border-r-0",
                isVariable &&
                  "border-violet-400 dark:border-violet-500 text-violet-700 dark:text-violet-300",
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* Jour — toujours fixe */}
              {isDayType &&
                DAYS.map((d) => (
                  <SelectItem key={d} value={d.toString()}>
                    {d}
                  </SelectItem>
                ))}

              {/* Mois / Année — valeurs fixes */}
              {!isDayType &&
                !isVariable &&
                type === "month" &&
                MONTHS.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()}>
                    {m.label}
                  </SelectItem>
                ))}
              {!isDayType &&
                !isVariable &&
                type === "year" &&
                Array.from({ length: 41 }, (_, i) => {
                  const y = new Date().getFullYear() - 20 + i;
                  return (
                    <SelectItem key={y} value={y.toString()}>
                      {y}
                    </SelectItem>
                  );
                })}

              {/* Mois / Année — variables SILAE */}
              {!isDayType &&
                isVariable &&
                dateVariables[type].map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {/* Bouton toggle — uniquement pour mois et année */}
          {!isDayType && (
            <button
              type="button"
              onClick={toggleMode}
              title={
                isVariable
                  ? "Passer en valeur fixe"
                  : "Passer en variable SILAE"
              }
              className={cn(
                "h-9  shrink-0 flex items-center gap-1 px-2 justify-center rounded-r-md border transition-all duration-150",
                isVariable
                  ? "bg-violet-500 border-violet-500 text-white hover:bg-violet-600 hover:border-violet-600"
                  : "bg-muted border-border text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
            >
              <Variable className="h-5 w-5" strokeWidth={2} />
              <span
                className={`text-xs font-bold ${isVariable ? "text-white" : "text-muted-foreground"}`}
              >
                Variable
              </span>
            </button>
          )}
        </div>

        {/* Indicateur de mode sous le champ */}
        {!isDayType && (
          <span
            className={cn(
              "text-[10px] font-medium leading-none",
              isVariable
                ? "text-violet-500 dark:text-violet-400"
                : "text-muted-foreground",
            )}
          >
            {isVariable ? "Variable SILAE" : "Valeur fixe"}
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      className={cn(
        "space-y-6 min-w-[410px] p-2 sm:p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-border",
        className,
      )}
    >
      {/* En-tête minimaliste */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium text-foreground">{label}</Label>
        </div>

        {/* Sélecteur de mode (seulement si les variables SILAE sont autorisées) */}
        {allowSilaeDateVariables && (
          <div className="flex gap-1 p-1 bg-muted rounded-lg">
            <Button
              variant={mode === "date_builder" ? "default" : "ghost"}
              size="sm"
              onClick={() => setMode("date_builder")}
              className="flex-1 h-8 text-xs font-normal"
            >
              Constructeur de date
            </Button>
            <Button
              variant={mode === "silae_variable" ? "default" : "ghost"}
              size="sm"
              onClick={() => setMode("silae_variable")}
              className="flex-1 h-8 text-xs font-normal"
            >
              Variable SILAE
            </Button>
          </div>
        )}

        {/* Aperçu du code généré */}
        <div className="p-3 rounded-lg border border-border bg-slate-200/50 dark:bg-slate-800/50">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">
              {mode === "silae_variable"
                ? "Variable sélectionnée"
                : "Aperçu de la date"}
            </span>
          </div>
          <code>
            {mode === "silae_variable"
              ? selectedSilaeVariable || "Aucune variable sélectionnée"
              : generateDateValue()}
          </code>
        </div>
      </div>

      {/* Contenu selon le mode */}
      {mode === "silae_variable" ? (
        /* Sélecteur de variables SILAE */
        <div className="space-y-3">
          <VariableSelector
            selectedVariable={selectedVariable}
            onVariableSelect={(variable) => {
              setSelectedSilaeVariable(variable.code);
            }}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            open={selectorOpen}
            setOpen={setSelectorOpen}
            frequentlyUsedVariables={frequentlyUsedDateVariables}
            filteredVariables={filteredDateVariables}
            className="w-full"
          />
        </div>
      ) : (
        /* Sélecteurs de composants de date */
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {renderComponentSelector(day, setDay, "day", "Jour")}
            {renderComponentSelector(month, setMonth, "month", "Mois")}
            {renderComponentSelector(year, setYear, "year", "Année")}
          </div>
        </div>
      )}
    </div>
  );
};
